import React, { useState, useMemo } from 'react';
import { X, ChevronLeft, ChevronRight, TrendingUp, TrendingDown, Loader2, Edit2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useUnifiedPrices } from '../contexts/UnifiedPriceContext';
import { calculateDailySwapCost } from '../constants/swapConfig';
import { formatSwapCostDisplay } from '../utils/swapCalculations';

interface Position {
  id: string;
  symbol: string;
  side: 'long' | 'short';
  amount: number;
  entryPrice: number;
  currentPrice: number;
  leverage: number;
  margin: number;
  liquidationPrice: number;
  unrealizedPnl: number;
  roi: number;
  takeProfit?: number | null;
  stopLoss?: number | null;
  accumulated_swap_cost?: number;
}

interface Order {
  id: string;
  symbol: string;
  side: 'buy' | 'sell';
  type: string;
  price: number | null;
  amount: number;
  leverage: number;
  status: string;
  created_at: string;
}

interface HistoryEntry {
  id: string;
  symbol: string;
  side: string;
  entryPrice: number;
  exitPrice: number;
  amount: number;
  pnl: number;
  closeTime: string;
  durationSeconds?: number;
  accumulatedSwapCost?: number;
}

interface OrderHistoryEntry {
  id: string;
  symbol: string;
  side: 'buy' | 'sell';
  type: string;
  price: number | null;
  amount: number;
  leverage: number;
  status: string;
  created_at: string;
  filled_at: string | null;
}

interface PositionsPanelProps {
  positions: Position[];
  openOrders: Order[];
  positionHistory: HistoryEntry[];
  orderHistory: OrderHistoryEntry[];
  onClosePosition: (positionId: string, livePrice: number) => void;
  onCancelOrder: (orderId: string) => Promise<boolean>;
  onCancelAllOrders: () => Promise<boolean>;
  onEditTPSL?: (positionId: string, type: 'takeProfit' | 'stopLoss') => void;
  loading?: boolean;
}

const PositionsPanel: React.FC<PositionsPanelProps> = ({
  positions,
  openOrders,
  positionHistory,
  orderHistory,
  onClosePosition,
  onCancelOrder,
  onCancelAllOrders,
  onEditTPSL,
  loading = false
}) => {
  const { t } = useTranslation();
  const { getPriceBySymbol, lastUpdateTime } = useUnifiedPrices();
  const [activeTab, setActiveTab] = useState<'positions' | 'pnl' | 'orders' | 'orderHistory' | 'tradeHistory'>('positions');
  const [currentPage, setCurrentPage] = useState(1);
  const [processingPositions, setProcessingPositions] = useState<Record<string, boolean>>({});
  const itemsPerPage = 5;

  const tabs = [
    { key: 'positions', label: `Positions(${positions.length})` },
    { key: 'pnl', label: 'P&L' },
    { key: 'orders', label: `Current Orders(${openOrders.length})` },
    { key: 'orderHistory', label: 'Order History' },
    { key: 'tradeHistory', label: 'Trade History' }
  ] as const;

  const positionsWithLiveData = useMemo(() => {
    return positions.map(position => {
      const livePrice = getPriceBySymbol(position.symbol) || position.currentPrice;
      const entryPrice = position.entryPrice || 0;
      const amount = position.amount || 0;
      const margin = position.margin || 0;
      const accumulatedSwapCost = position.accumulated_swap_cost || 0;

      let unrealizedPnl = 0;
      if (position.side === 'long') {
        unrealizedPnl = (livePrice - entryPrice) * amount;
      } else {
        unrealizedPnl = (entryPrice - livePrice) * amount;
      }

      const netUnrealizedPnl = unrealizedPnl - accumulatedSwapCost;
      const roi = margin > 0 ? (netUnrealizedPnl / margin) * 100 : 0;

      return {
        ...position,
        currentPrice: livePrice,
        unrealizedPnl: netUnrealizedPnl,
        roi,
        accumulatedSwapCost
      };
    });
  }, [positions, getPriceBySymbol, lastUpdateTime]);

  const totalUnrealizedPnl = positionsWithLiveData.reduce((sum, pos) => sum + (pos.unrealizedPnl || 0), 0);

  const handleClosePosition = async (positionId: string) => {
    const position = positionsWithLiveData.find(p => p.id === positionId);
    if (!position) return;

    setProcessingPositions(prev => ({ ...prev, [positionId]: true }));
    try {
      await onClosePosition(positionId, position.currentPrice);
    } finally {
      setProcessingPositions(prev => ({ ...prev, [positionId]: false }));
    }
  };

  const formatPrice = (price: number, symbol: string) => {
    const isJpyPair = symbol.includes('JPY');
    const isForexPair = symbol.includes('EUR') || symbol.includes('GBP') || symbol.includes('AUD') ||
      symbol.includes('NZD') || symbol.includes('CAD') || symbol.includes('CHF') ||
      (symbol.includes('USD') && !symbol.includes('USDT') && !symbol.endsWith('/USD'));

    if (isJpyPair) return price.toFixed(3);
    if (isForexPair || (price >= 0.1 && price < 10)) return price.toFixed(5);
    if (price >= 10000) return price.toFixed(2);
    if (price >= 1000) return price.toFixed(2);
    if (price >= 100) return price.toFixed(3);
    if (price >= 10) return price.toFixed(4);
    if (price >= 1) return price.toFixed(5);
    return price.toFixed(6);
  };

  const formatDuration = (seconds: number) => {
    if (seconds < 60) return `${seconds}s`;
    if (seconds < 3600) return `${Math.floor(seconds / 60)}m`;
    if (seconds < 86400) return `${Math.floor(seconds / 3600)}h`;
    return `${Math.floor(seconds / 86400)}d`;
  };

  return (
    <div className="h-full flex flex-col bg-white" translate="no">
      <div className="flex items-center justify-between border-b border-slate-200 px-4">
        <div className="flex items-center gap-1 overflow-x-auto hide-scrollbar">
          {tabs.map(tab => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              className={`px-3 py-3 text-xs font-medium whitespace-nowrap transition-colors relative ${
                activeTab === tab.key
                  ? 'text-slate-900'
                  : 'text-slate-500 hover:text-slate-700'
              }`}
            >
              {tab.label}
              {activeTab === tab.key && (
                <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-blue-500" />
              )}
            </button>
          ))}
        </div>

        <div className="flex items-center gap-2 text-xs">
          <span className="text-slate-500">Unrealized P&L:</span>
          <span className={`font-mono font-medium ${totalUnrealizedPnl >= 0 ? 'text-green-500' : 'text-red-500'}`}>
            {totalUnrealizedPnl >= 0 ? '+' : ''}{totalUnrealizedPnl.toFixed(4)} USDT
          </span>
        </div>
      </div>

      <div className="flex-1 overflow-auto">
        {activeTab === 'positions' && (
          <>
            {positionsWithLiveData.length > 0 ? (
              <table className="w-full text-xs">
                <thead className="bg-slate-50 sticky top-0">
                  <tr className="text-slate-500">
                    <th className="text-left py-2 px-3 font-medium">Contracts</th>
                    <th className="text-right py-2 px-3 font-medium">Qty</th>
                    <th className="text-right py-2 px-3 font-medium">Value</th>
                    <th className="text-right py-2 px-3 font-medium">Entry Price</th>
                    <th className="text-right py-2 px-3 font-medium">Mark Price</th>
                    <th className="text-right py-2 px-3 font-medium">Liq. Price</th>
                    <th className="text-right py-2 px-3 font-medium">Position Margin</th>
                    <th className="text-right py-2 px-3 font-medium">Unrealized P&L(%)</th>
                    <th className="text-center py-2 px-3 font-medium">TP/SL</th>
                    <th className="text-center py-2 px-3 font-medium">Close</th>
                  </tr>
                </thead>
                <tbody>
                  {positionsWithLiveData.map(position => (
                    <tr key={position.id} className="border-b border-slate-100 hover:bg-slate-50">
                      <td className="py-2 px-3">
                        <div className="flex items-center gap-2">
                          <span className="font-medium text-slate-900">{position.symbol}</span>
                          <span className={`text-xs px-1.5 py-0.5 rounded ${
                            position.side === 'long'
                              ? 'bg-green-100 text-green-600'
                              : 'bg-red-100 text-red-600'
                          }`}>
                            {position.side === 'long' ? 'Long' : 'Short'} {position.leverage}x
                          </span>
                        </div>
                      </td>
                      <td className={`text-right py-2 px-3 font-mono ${
                        position.side === 'long' ? 'text-green-500' : 'text-red-500'
                      }`}>
                        {position.amount.toFixed(4)}
                      </td>
                      <td className="text-right py-2 px-3 font-mono text-slate-600">
                        {(position.amount * position.currentPrice).toFixed(2)}
                      </td>
                      <td className="text-right py-2 px-3 font-mono text-slate-900">
                        {formatPrice(position.entryPrice, position.symbol)}
                      </td>
                      <td className="text-right py-2 px-3 font-mono text-slate-900">
                        {formatPrice(position.currentPrice, position.symbol)}
                      </td>
                      <td className="text-right py-2 px-3 font-mono text-orange-500">
                        {formatPrice(position.liquidationPrice, position.symbol)}
                      </td>
                      <td className="text-right py-2 px-3 font-mono text-slate-600">
                        {position.margin.toFixed(2)}
                      </td>
                      <td className="text-right py-2 px-3">
                        <div className={`font-mono ${position.unrealizedPnl >= 0 ? 'text-green-500' : 'text-red-500'}`}>
                          {position.unrealizedPnl >= 0 ? '+' : ''}{position.unrealizedPnl.toFixed(4)}
                          <span className="text-xs ml-1">({position.roi >= 0 ? '+' : ''}{position.roi.toFixed(2)}%)</span>
                        </div>
                      </td>
                      <td className="text-center py-2 px-3">
                        <div className="flex flex-col items-center gap-0.5">
                          {position.takeProfit ? (
                            <span className="text-green-500 font-mono text-xs flex items-center gap-1">
                              TP: {formatPrice(position.takeProfit, position.symbol)}
                              {onEditTPSL && (
                                <button onClick={() => onEditTPSL(position.id, 'takeProfit')} className="hover:text-green-600">
                                  <Edit2 size={10} />
                                </button>
                              )}
                            </span>
                          ) : (
                            <button
                              onClick={() => onEditTPSL?.(position.id, 'takeProfit')}
                              className="text-slate-400 hover:text-green-500 text-xs"
                            >
                              +TP
                            </button>
                          )}
                          {position.stopLoss ? (
                            <span className="text-red-500 font-mono text-xs flex items-center gap-1">
                              SL: {formatPrice(position.stopLoss, position.symbol)}
                              {onEditTPSL && (
                                <button onClick={() => onEditTPSL(position.id, 'stopLoss')} className="hover:text-red-600">
                                  <Edit2 size={10} />
                                </button>
                              )}
                            </span>
                          ) : (
                            <button
                              onClick={() => onEditTPSL?.(position.id, 'stopLoss')}
                              className="text-slate-400 hover:text-red-500 text-xs"
                            >
                              +SL
                            </button>
                          )}
                        </div>
                      </td>
                      <td className="text-center py-2 px-3">
                        <button
                          onClick={() => handleClosePosition(position.id)}
                          disabled={processingPositions[position.id]}
                          className="px-2 py-1 text-xs text-slate-500 hover:text-red-500 hover:bg-red-50 rounded transition-colors disabled:opacity-50"
                        >
                          {processingPositions[position.id] ? (
                            <Loader2 size={12} className="animate-spin" />
                          ) : (
                            <X size={14} />
                          )}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <div className="flex flex-col items-center justify-center h-full py-12 text-slate-400">
                <p className="text-sm">Please log in or sign up first.</p>
                <p className="text-xs mt-1">Your positions will appear here.</p>
              </div>
            )}
          </>
        )}

        {activeTab === 'orders' && (
          <>
            {openOrders.length > 0 ? (
              <table className="w-full text-xs">
                <thead className="bg-slate-50 sticky top-0">
                  <tr className="text-slate-500">
                    <th className="text-left py-2 px-3 font-medium">Time</th>
                    <th className="text-left py-2 px-3 font-medium">Symbol</th>
                    <th className="text-left py-2 px-3 font-medium">Type</th>
                    <th className="text-left py-2 px-3 font-medium">Side</th>
                    <th className="text-right py-2 px-3 font-medium">Price</th>
                    <th className="text-right py-2 px-3 font-medium">Amount</th>
                    <th className="text-center py-2 px-3 font-medium">Cancel</th>
                  </tr>
                </thead>
                <tbody>
                  {openOrders.map(order => (
                    <tr key={order.id} className="border-b border-slate-100 hover:bg-slate-50">
                      <td className="py-2 px-3 font-mono text-slate-600">
                        {new Date(order.created_at).toLocaleTimeString()}
                      </td>
                      <td className="py-2 px-3 font-medium text-slate-900">{order.symbol}</td>
                      <td className="py-2 px-3 text-slate-600 capitalize">{order.type}</td>
                      <td className="py-2 px-3">
                        <span className={`px-1.5 py-0.5 rounded text-xs ${
                          order.side === 'buy'
                            ? 'bg-green-100 text-green-600'
                            : 'bg-red-100 text-red-600'
                        }`}>
                          {order.side.toUpperCase()}
                        </span>
                      </td>
                      <td className="text-right py-2 px-3 font-mono text-slate-900">
                        {order.price?.toFixed(2) || 'Market'}
                      </td>
                      <td className="text-right py-2 px-3 font-mono text-slate-600">
                        {order.amount.toFixed(4)}
                      </td>
                      <td className="text-center py-2 px-3">
                        <button
                          onClick={() => onCancelOrder(order.id)}
                          className="px-2 py-1 text-slate-500 hover:text-red-500 hover:bg-red-50 rounded transition-colors"
                        >
                          <X size={14} />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <div className="flex flex-col items-center justify-center h-full py-12 text-slate-400">
                <p className="text-sm">No open orders</p>
              </div>
            )}
          </>
        )}

        {activeTab === 'tradeHistory' && (
          <>
            {positionHistory.length > 0 ? (
              <table className="w-full text-xs">
                <thead className="bg-slate-50 sticky top-0">
                  <tr className="text-slate-500">
                    <th className="text-left py-2 px-3 font-medium">Time</th>
                    <th className="text-left py-2 px-3 font-medium">Symbol</th>
                    <th className="text-left py-2 px-3 font-medium">Side</th>
                    <th className="text-right py-2 px-3 font-medium">Entry</th>
                    <th className="text-right py-2 px-3 font-medium">Exit</th>
                    <th className="text-right py-2 px-3 font-medium">Amount</th>
                    <th className="text-right py-2 px-3 font-medium">P&L</th>
                  </tr>
                </thead>
                <tbody>
                  {positionHistory.slice(0, 20).map(entry => (
                    <tr key={entry.id} className="border-b border-slate-100 hover:bg-slate-50">
                      <td className="py-2 px-3 font-mono text-slate-600">
                        {new Date(entry.closeTime).toLocaleString()}
                      </td>
                      <td className="py-2 px-3 font-medium text-slate-900">{entry.symbol}</td>
                      <td className="py-2 px-3">
                        <span className={`px-1.5 py-0.5 rounded text-xs ${
                          entry.side === 'long'
                            ? 'bg-green-100 text-green-600'
                            : 'bg-red-100 text-red-600'
                        }`}>
                          {entry.side.toUpperCase()}
                        </span>
                      </td>
                      <td className="text-right py-2 px-3 font-mono text-slate-900">
                        {formatPrice(entry.entryPrice, entry.symbol)}
                      </td>
                      <td className="text-right py-2 px-3 font-mono text-slate-900">
                        {formatPrice(entry.exitPrice, entry.symbol)}
                      </td>
                      <td className="text-right py-2 px-3 font-mono text-slate-600">
                        {entry.amount.toFixed(4)}
                      </td>
                      <td className={`text-right py-2 px-3 font-mono ${
                        entry.pnl >= 0 ? 'text-green-500' : 'text-red-500'
                      }`}>
                        {entry.pnl >= 0 ? '+' : ''}{entry.pnl.toFixed(4)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <div className="flex flex-col items-center justify-center h-full py-12 text-slate-400">
                <p className="text-sm">No trade history</p>
              </div>
            )}
          </>
        )}

        {activeTab === 'pnl' && (
          <div className="p-4">
            {positionHistory.length > 0 ? (
              <div className="space-y-4">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="bg-slate-50 rounded-lg p-4">
                    <p className="text-xs text-slate-500 mb-1">Total Realized P&L</p>
                    <p className={`text-lg font-semibold font-mono ${
                      positionHistory.reduce((sum, h) => sum + h.pnl, 0) >= 0 ? 'text-green-500' : 'text-red-500'
                    }`}>
                      {positionHistory.reduce((sum, h) => sum + h.pnl, 0) >= 0 ? '+' : ''}
                      {positionHistory.reduce((sum, h) => sum + h.pnl, 0).toFixed(4)} USDT
                    </p>
                  </div>
                  <div className="bg-slate-50 rounded-lg p-4">
                    <p className="text-xs text-slate-500 mb-1">Total Trades</p>
                    <p className="text-lg font-semibold text-slate-900">{positionHistory.length}</p>
                  </div>
                  <div className="bg-slate-50 rounded-lg p-4">
                    <p className="text-xs text-slate-500 mb-1">Winning Trades</p>
                    <p className="text-lg font-semibold text-green-500">
                      {positionHistory.filter(h => h.pnl >= 0).length}
                    </p>
                  </div>
                  <div className="bg-slate-50 rounded-lg p-4">
                    <p className="text-xs text-slate-500 mb-1">Losing Trades</p>
                    <p className="text-lg font-semibold text-red-500">
                      {positionHistory.filter(h => h.pnl < 0).length}
                    </p>
                  </div>
                </div>

                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="bg-slate-50 rounded-lg p-4">
                    <p className="text-xs text-slate-500 mb-1">Win Rate</p>
                    <p className="text-lg font-semibold text-slate-900">
                      {positionHistory.length > 0
                        ? ((positionHistory.filter(h => h.pnl >= 0).length / positionHistory.length) * 100).toFixed(1)
                        : 0}%
                    </p>
                  </div>
                  <div className="bg-slate-50 rounded-lg p-4">
                    <p className="text-xs text-slate-500 mb-1">Avg. Win</p>
                    <p className="text-lg font-semibold text-green-500 font-mono">
                      +{positionHistory.filter(h => h.pnl >= 0).length > 0
                        ? (positionHistory.filter(h => h.pnl >= 0).reduce((sum, h) => sum + h.pnl, 0) /
                           positionHistory.filter(h => h.pnl >= 0).length).toFixed(4)
                        : '0.0000'}
                    </p>
                  </div>
                  <div className="bg-slate-50 rounded-lg p-4">
                    <p className="text-xs text-slate-500 mb-1">Avg. Loss</p>
                    <p className="text-lg font-semibold text-red-500 font-mono">
                      {positionHistory.filter(h => h.pnl < 0).length > 0
                        ? (positionHistory.filter(h => h.pnl < 0).reduce((sum, h) => sum + h.pnl, 0) /
                           positionHistory.filter(h => h.pnl < 0).length).toFixed(4)
                        : '0.0000'}
                    </p>
                  </div>
                  <div className="bg-slate-50 rounded-lg p-4">
                    <p className="text-xs text-slate-500 mb-1">Best Trade</p>
                    <p className="text-lg font-semibold text-green-500 font-mono">
                      +{Math.max(0, ...positionHistory.map(h => h.pnl)).toFixed(4)}
                    </p>
                  </div>
                </div>

                <div className="mt-4">
                  <h3 className="text-sm font-medium text-slate-700 mb-2">Recent P&L</h3>
                  <div className="space-y-1">
                    {positionHistory.slice(0, 10).map(entry => (
                      <div key={entry.id} className="flex items-center justify-between py-2 border-b border-slate-100">
                        <div className="flex items-center gap-2">
                          <span className="text-xs font-medium text-slate-900">{entry.symbol}</span>
                          <span className={`text-xs px-1.5 py-0.5 rounded ${
                            entry.side === 'long' ? 'bg-green-100 text-green-600' : 'bg-red-100 text-red-600'
                          }`}>
                            {entry.side.toUpperCase()}
                          </span>
                        </div>
                        <div className="text-right">
                          <span className={`text-xs font-mono ${entry.pnl >= 0 ? 'text-green-500' : 'text-red-500'}`}>
                            {entry.pnl >= 0 ? '+' : ''}{entry.pnl.toFixed(4)} USDT
                          </span>
                          <p className="text-xs text-slate-400">
                            {new Date(entry.closeTime).toLocaleDateString()}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center py-12 text-slate-400">
                <p className="text-sm">No trading history yet</p>
                <p className="text-xs mt-1">Your P&L statistics will appear here after closing trades.</p>
              </div>
            )}
          </div>
        )}

        {activeTab === 'orderHistory' && (
          <>
            {orderHistory.length > 0 ? (
              <table className="w-full text-xs">
                <thead className="bg-slate-50 sticky top-0">
                  <tr className="text-slate-500">
                    <th className="text-left py-2 px-3 font-medium">Time</th>
                    <th className="text-left py-2 px-3 font-medium">Symbol</th>
                    <th className="text-left py-2 px-3 font-medium">Type</th>
                    <th className="text-left py-2 px-3 font-medium">Side</th>
                    <th className="text-right py-2 px-3 font-medium">Price</th>
                    <th className="text-right py-2 px-3 font-medium">Amount</th>
                    <th className="text-center py-2 px-3 font-medium">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {orderHistory.slice(0, 50).map(order => (
                    <tr key={order.id} className="border-b border-slate-100 hover:bg-slate-50">
                      <td className="py-2 px-3 font-mono text-slate-600">
                        {new Date(order.filled_at || order.created_at).toLocaleString()}
                      </td>
                      <td className="py-2 px-3 font-medium text-slate-900">{order.symbol}</td>
                      <td className="py-2 px-3 text-slate-600 capitalize">{order.type}</td>
                      <td className="py-2 px-3">
                        <span className={`px-1.5 py-0.5 rounded text-xs ${
                          order.side === 'buy'
                            ? 'bg-green-100 text-green-600'
                            : 'bg-red-100 text-red-600'
                        }`}>
                          {order.side.toUpperCase()}
                        </span>
                      </td>
                      <td className="text-right py-2 px-3 font-mono text-slate-900">
                        {order.price?.toFixed(2) || 'Market'}
                      </td>
                      <td className="text-right py-2 px-3 font-mono text-slate-600">
                        {order.amount.toFixed(4)}
                      </td>
                      <td className="text-center py-2 px-3">
                        <span className={`px-1.5 py-0.5 rounded text-xs ${
                          order.status === 'filled'
                            ? 'bg-green-100 text-green-600'
                            : 'bg-slate-100 text-slate-600'
                        }`}>
                          {order.status.charAt(0).toUpperCase() + order.status.slice(1)}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <div className="flex flex-col items-center justify-center h-full py-12 text-slate-400">
                <p className="text-sm">No order history</p>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default PositionsPanel;
